import { VerifyDuplicateAgencyMutationVariables } from '@sortlist-frontend/data-fetching/core-types';
import { showToast } from '@sortlist-frontend/design-system';
import { Monitor } from '@sortlist-frontend/mlm';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { useIntercom } from '@sortlist-frontend/user-tools';
import { HttpException } from '@sortlist-frontend/utils';
import { useMutation } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { route } from 'nextjs-routes';

import { api } from '_core/api/api-nextjs-proxy';
import { usePublicAppContext } from '_core/context/public-app-context';
import {
  VerifyDuplicateAgencyError,
  VerifyDuplicateAgencyResult,
} from '_pages/api/proxy/agencies/verify-duplicate-agency';

const PROXY_URL = route({ pathname: '/api/proxy/agencies/verify-duplicate-agency' });

const verifyDuplicateAgency = async (
  variables: VerifyDuplicateAgencyMutationVariables,
  config?: AxiosRequestConfig,
): Promise<VerifyDuplicateAgencyResult> => {
  const returnObject = await api.post(PROXY_URL, variables, config);

  return returnObject?.data;
};

export const VERIFY_DUPLICATE_AGENCY_MUTATION_KEY = ['verifyDuplicateAgency'];

export function useVerifyDuplicateAgency() {
  const { domainInfo } = usePublicAppContext();
  const baseURL = domainInfo?.getOriginUrl() ?? '';
  const { showIntercom } = useIntercom();
  const { t } = useTranslation(['common', 'agency']);

  return useMutation({
    mutationKey: VERIFY_DUPLICATE_AGENCY_MUTATION_KEY,
    mutationFn: (args: VerifyDuplicateAgencyMutationVariables & { claimSessionId: string }) => {
      return verifyDuplicateAgency(args, { baseURL });
    },
    onError: (error: HttpException, variables: VerifyDuplicateAgencyMutationVariables & { claimSessionId: string }) => {
      const details = error.contexts?.details as NonNullable<
        VerifyDuplicateAgencyError['errors'][0]['extensions']
      >['details'];
      const { email, phone, website } = details ?? {};

      const conflictingAgencySlugs = [
        ...new Set(
          [email, phone, website].flatMap((arr) => arr ?? []).flatMap((item) => Object.keys(item.conflict_with ?? {})),
        ),
      ];

      const agenciesPerField = {
        email: email?.flatMap((item) => Object.keys(item.conflict_with ?? {})) ?? [],
        phone: phone?.flatMap((item) => Object.keys(item.conflict_with ?? {})) ?? [],
        website: website?.flatMap((item) => Object.keys(item.conflict_with ?? {})) ?? [],
      };

      const isConflictingWithDeletedAgency = [email, phone, website]
        .flatMap((arr) => arr ?? [])
        .some((item) => Object.values(item.conflict_with ?? {}).includes('deleted'));

      Monitor.captureMessage(`Duplicate agency found during onboarding`, {
        tags: {
          claimSessionId: variables.claimSessionId,
          email: email != null,
          phone: phone != null,
          website: website != null,
          isConflictingWithDeletedAgency,
        },
        extra: {
          variables,
          details,
        },
      });

      // If more than one agency is conflicting, we show a generic error
      if (conflictingAgencySlugs.length > 1 || isConflictingWithDeletedAgency) {
        showToast({
          timeout: Infinity,
          title: t('agency:claim.form.errors.takenMultiple'),
          description: (
            <div className="text-sm">
              <ul>
                {agenciesPerField.email.length > 0 && (
                  <li>{t('agency:claim.form.errors.emailTaken2', { count: agenciesPerField.email.length })}</li>
                )}
                {agenciesPerField.phone.length > 0 && (
                  <li>{t('agency:claim.form.errors.phoneTaken2', { count: agenciesPerField.phone.length })}</li>
                )}
                {agenciesPerField.website.length > 0 && (
                  <li>{t('agency:claim.form.errors.websiteTaken2', { count: agenciesPerField.website.length })}</li>
                )}
              </ul>
              <p className="text-sm">
                <Trans i18nKey="agency:claim.form.errors.pleaseContactSupport">
                  Please&nbsp;
                  <span role="none" onClick={showIntercom} className="underline cursor-pointer">
                    contact our support team
                  </span>
                  &nbsp;to help you resolve this issue.
                </Trans>
              </p>
            </div>
          ),
          classNames: {
            base: 'min-w-[500px]',
          },
          color: 'danger',
        });
      } else {
        showToast({
          timeout: Infinity,
          title: t('agency:claim.form.errors.fieldsFoundUsed', {
            fields: Object.entries(agenciesPerField)
              .filter(([_, value]) => value.length > 0)
              .map(([key]) => key)
              .join(', '),
          }),
          description: (
            <p className="text-sm pt-2">
              <Trans i18nKey="agency:claim.form.errors.meantToJoin">
                Maybe you meant to&nbsp;
                <a href={`/claim/agency/confirm/${conflictingAgencySlugs[0]}`} className="text-inherit underline">
                  join the agency
                </a>
                &nbsp; that already exists?
              </Trans>
            </p>
          ),
          classNames: {
            base: 'min-w-[500px]',
          },
          color: 'danger',
        });
      }
    },
  });
}
